/**
 * scopeTabs — the three steps the scope document is split into, and everything derivable from that
 * split. Kept free of React so it is unit-testable: the web suite runs `environment: "node"` with
 * no jsdom, so pure extraction is the only way to test any of this.
 *
 * The tabs are steps, not gates. Every one is reachable and editable from the first moment, by the
 * user and by the agent alike — the sequencing lives in the agent's guidance, not in a lock. That
 * is deliberate: the steps are highly interdependent (objectives come out of the hypotheses, the
 * audience shapes the objectives), and staged disclosure only works on steps that are not, or
 * people end up trapped navigating backwards. The row deliberately reports nothing about how
 * complete a step is — the checklist rail beside the document already says that, per section and
 * in more detail. The one thing it does show is the step the agent is writing into
 * (`streamingScopeTabs`), which nothing else on screen can: argument generation takes seconds, and
 * a step filling in off screen otherwise looks like nothing happening at all.
 */

import type { Study } from "@/chat";
import type { StudyStatus } from "./api";
import type { ScopeStreamPreview } from "./scopeStreamPreview";
import { hasTimeline } from "./timeline";

export const SCOPE_TABS = ["thesis", "audience", "guide"] as const;
export type ScopeTab = (typeof SCOPE_TABS)[number];

export const SCOPE_TAB_LABELS: Record<ScopeTab, string> = {
  thesis: "Thesis",
  audience: "Audience",
  guide: "Interview guide",
};

/** Where the pre-`audience_description` upcast put study-wide criteria (see `scope_versions.py`). */
const PARTICIPANT_REQUIREMENTS_HEADER = "Participant requirements (all segments):";

/**
 * Whether the study says who it wants to hear from — the client's copy of the audience half of the
 * backend's `ensure_study_scope_ready`.
 *
 * One function because there are two callers that must agree: the launch/preview gate in
 * `StudyAuthoring` and the checklist rail's Audience row. When they drifted, a study the API would
 * happily launch showed Launch disabled and its required section permanently unfilled, with no way
 * to tell what was missing — so the rule lives here once rather than being restated at each gate.
 *
 * Four accepted shapes, because four exist in production:
 *  - a legacy (v1/v2) scope, whose audience is still `audience_criteria` or a segment's `criteria`;
 *  - the study-wide `audience_description`, which is where the v2→v3 upcast now puts those criteria;
 *  - any one segment's own description;
 *  - the research context, for studies migrated before `audience_description` existed, whose
 *    criteria the old upcast appended under `PARTICIPANT_REQUIREMENTS_HEADER`.
 *
 * Read as a set rather than selected by `scope_draft_schema_version`, because that version does not
 * always describe the fields beside it. On a launched legacy study the first edit moves the draft to
 * v3 while the materialized columns stay v1 — correctly, since after launch they belong to the
 * active publication — and `StudyResponse` carries the draft's version next to those columns. The
 * selector would then look for v3 names on a v1 row and call the audience missing. The shapes do not
 * co-occur (the upcast clears what it moves), so accepting all of them decides the same studies the
 * server does, which reaches that answer by upcasting before it looks rather than by branching.
 */
export function hasAudienceDefinition(study: Study): boolean {
  return (
    (study.audience_description?.trim() ?? "") !== "" ||
    study.audience_criteria.length > 0 ||
    study.audience_segments.some(
      (segment) => (segment.description?.trim() ?? "") !== "" || (segment.criteria?.length ?? 0) > 0
    ) ||
    study.context.includes(PARTICIPANT_REQUIREMENTS_HEADER)
  );
}

/**
 * Whether the study says what it is trying to find out — the thesis half of the backend's
 * `ensure_study_scope_ready`, with the same two callers as `hasAudienceDefinition`.
 *
 * A v1 scope kept its thesis under `key_questions`; the v2→v3 upcast is what renamed them, and the
 * backend gate reads a legacy study through that upcast. So both names count here, or a legacy
 * study that has said exactly what the gate asks would show its Launch disabled with the section
 * it filled in marked empty.
 */
export function hasThesis(study: Study): boolean {
  return study.hypotheses.length > 0 || study.key_questions.length > 0;
}

/**
 * What the study still needs before Preview or Launch, in document order: the backend's
 * `ensure_study_scope_ready`. Like the backend, the thesis and the interviewer brief are asked only
 * before launch; a study launched without one keeps running on it, and asking would block previewing
 * an unrelated change.
 */
export function missingLaunchScope(study: Study | undefined, status: StudyStatus): string[] {
  const prelaunch = status === "draft" || status === "requested";
  const missing: string[] = [];
  if (!study?.context.trim()) missing.push("research context");
  if (prelaunch && !(study ? hasThesis(study) : false)) missing.push("at least one hypothesis");
  if (!(study ? hasAudienceDefinition(study) : false)) missing.push("an audience description");
  if (prelaunch && !study?.interviewer_brief?.trim()) missing.push("an interviewer brief");
  if ((study?.objectives.length ?? 0) === 0) missing.push("at least one objective");
  return missing;
}

/**
 * Which tab owns each `<Section>` of the document.
 *
 * `key-questions` is the legacy (scope v1) framing that `hypotheses` replaced, so the two share a
 * tab — otherwise opening a legacy study would hide a section with no way to reach it.
 */
const SECTION_TABS: Readonly<Record<string, ScopeTab>> = {
  "research-context": "thesis",
  timeline: "thesis",
  "key-questions": "thesis",
  hypotheses: "thesis",
  audience: "audience",
  "interviewer-brief": "guide",
  "discussion-guide": "guide",
};

export function scopeTabForSection(sectionId: string): ScopeTab | null {
  return SECTION_TABS[sectionId] ?? null;
}

export function isScopeTab(value: string | undefined): value is ScopeTab {
  return value != null && (SCOPE_TABS as readonly string[]).includes(value);
}

/** Whether a document section's tab is currently visible. Undefined preserves the full document. */
export function isScopeTabVisible(active: ScopeTab | undefined, tab: ScopeTab): boolean {
  return active === undefined || active === tab;
}

export interface ScopeSectionChecklistItem {
  id: string;
  label: string;
  /** Required to preview/launch — mirrors the backend's `ensure_study_scope_ready`. */
  required: boolean;
  filled: boolean;
}

/**
 * The document's sections with their done/required state, in document order. Drives the checklist
 * rail beside the document, which is the one place completeness is reported.
 */
export function scopeSectionChecklist(study: Study): ScopeSectionChecklistItem[] {
  const usesLegacyQuestions = (study.scope_draft_schema_version ?? 1) < 2;
  return [
    {
      id: "research-context",
      label: "Research context",
      required: true,
      filled: study.context.trim() !== "",
    },
    {
      id: "timeline",
      label: "Timeline",
      required: false,
      filled: hasTimeline(study.timeline),
    },
    {
      id: usesLegacyQuestions ? "key-questions" : "hypotheses",
      label: usesLegacyQuestions ? "Key questions" : "Hypotheses",
      required: true,
      filled: hasThesis(study),
    },
    {
      id: "audience",
      label: "Audience",
      required: true,
      // Countries is optional and the interview count always has a valid default.
      filled: hasAudienceDefinition(study),
    },
    {
      id: "interviewer-brief",
      label: "Interviewer brief",
      required: true,
      filled: (study.interviewer_brief?.trim() ?? "") !== "",
    },
    {
      id: "discussion-guide",
      label: "Interview guide",
      required: true,
      filled: study.objectives.length > 0,
    },
  ];
}

/**
 * The tabs an in-flight `study_scope_edit` is writing into, in document order.
 *
 * A streamed title is deliberately not activity in any tab: the study name sits above the row, on
 * screen from every step, so it is never a reason to draw attention to one of them.
 */
export function streamingScopeTabs(preview: ScopeStreamPreview | undefined): ScopeTab[] {
  if (!preview || preview.isEmpty) return [];
  const touched = new Set<ScopeTab>();
  if (preview.context != null || preview.timeline != null || preview.hypotheses.length > 0) {
    touched.add("thesis");
  }
  if (
    preview.audience_description != null ||
    preview.countries != null ||
    preview.audience_segments.length > 0
  ) {
    touched.add("audience");
  }
  if (preview.interviewer_brief != null || preview.objectives.length > 0) touched.add("guide");
  return SCOPE_TABS.filter((tab) => touched.has(tab));
}

/**
 * The tab to move the user to as the agent writes, or null to leave them where they are.
 *
 * Splitting the document into tabs would otherwise quietly undo the streamed ghost rows: argument
 * generation runs for seconds, and if it lands in a tab the user cannot see, the screen looks dead
 * for exactly as long as the feature was built to fill. So the view follows the write — but only
 * when following is free. A user with a cursor in a field, or one who has picked a tab since the
 * run began, has said where they want to be; the step's activity dot tells them the rest.
 *
 * Of a multi-section write we follow the furthest tab in document order, because the streamed
 * prefix grows in that order — the furthest one is where the agent is writing *now*.
 */
export function scopeTabForStreamingEdit({
  preview,
  active,
  userPinned,
  editing,
}: {
  preview: ScopeStreamPreview | undefined;
  active: ScopeTab;
  userPinned: boolean;
  editing: boolean;
}): ScopeTab | null {
  if (userPinned || editing) return null;
  const touched = streamingScopeTabs(preview);
  const target = touched[touched.length - 1];
  return target != null && target !== active ? target : null;
}
